import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Two-line identity cell: the readable name on top, quieter context beneath.
 *
 * Most of our tables key rows on an opaque id — a Cinch company/location UUID,
 * an integration key path — while the thing an operator recognises is the
 * name. This renders the pair in the house arrangement so every table shows
 * identity the same way:
 *
 *   Acme Auto Care          ← name (or `fallback` when we couldn't resolve it)
 *   grow · 8f3c…            ← context: parent trail, refs, numbers
 *
 * `fallback` is the point of the component, not a detail: names resolve from
 * a dimension read that can legitimately come back empty (brand-new entity,
 * deleted row, Snowflake down). Passing the raw id as `fallback` means the row
 * degrades to what it used to show instead of rendering blank.
 *
 * Reach for `<EntityName name={r.company_name} fallback={r.key_path} … />`
 * rather than re-rolling the flex-col + muted-xs stack.
 */
export function EntityName({
  name,
  fallback,
  context,
  badge,
  className,
}: {
  /** The readable name. Null/empty falls back to `fallback`. */
  name?: string | null;
  /** Shown when `name` is missing — pass the raw id/key, never "". */
  fallback: string;
  /** Muted second line: parent trail, external ref, store number. */
  context?: ReactNode;
  /** Rendered inline after the name — a StatusBadge, usually. */
  badge?: ReactNode;
  className?: string;
}) {
  const resolved = name?.trim();
  return (
    <div className={cn("flex flex-col", className)}>
      <span className="flex items-center gap-2 font-medium">
        {resolved || fallback}
        {badge}
      </span>
      {context ? (
        <span className="text-xs text-muted-foreground">{context}</span>
      ) : null}
    </div>
  );
}
